import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { startDocumentOCR } from "../api";
import { useProfileStore } from "../store/profileStore";
import type { StartOCRResponse } from "../types";
import { showNotification } from "@mantine/notifications";
import { pollUntilComplete } from "@/utils/api/jobApi";
import { emitDebugEvent } from "@utils/debug/eventIntegration";

export const useStartOCRJob = (
  docId: string
): UseMutationResult<StartOCRResponse, unknown, void, unknown> => {
  const queryClient = useQueryClient();

  const {
    setOcrProcessingDocId,
    clearOcrProcessingDocId,
    refetch,
    documents,
    userId,
  } = useProfileStore();

  const handleSuccess = async (response: StartOCRResponse): Promise<void> => {
    const jobId = response.jobId;

    setOcrProcessingDocId(docId);

    try {
      const finalStatus = await pollUntilComplete(jobId);

      if (finalStatus === "completed") {
        // Get document info for event context
        const document = documents.find((doc) => doc.docId === docId);

        // Emit debug event for document processing completed
        emitDebugEvent("action", "DOCUMENT_PROCESSING_COMPLETED", "web", {
          fileName: document?.label?.substring(0, 100) || "unknown",
          jobId: jobId,
          clientId: userId || "unknown",
          formType: document?.key || "unknown",
        });

        try {
          // Refetch the entire profile to get updated documents with new OCR data
          await refetch();

          showNotification({
            title: "OCR Complete",
            message: "Your document was successfully processed.",
            color: "green",
          });
        } catch (fetchErr) {
          console.error("Failed to refetch profile:", fetchErr);
          showNotification({
            title: "Profile Refresh Failed",
            message: "Document processed, but profile could not be refreshed.",
            color: "yellow",
          });
        }
      }

      if (finalStatus === "failed") {
        showNotification({
          title: "OCR Failed",
          message: "There was a problem processing your document.",
          color: "red",
        });
      }
    } catch (err) {
      console.error("OCR polling failed:", err);
      showNotification({
        title: "OCR Timeout",
        message: "Document processing timed out or failed.",
        color: "red",
      });
    } finally {
      clearOcrProcessingDocId();
      queryClient.invalidateQueries({ queryKey: ["ocr-status", docId] });
    }
  };

  const handleError = (error: unknown): void => {
    console.error("Failed to start OCR job:", error);
    showNotification({
      title: "OCR Job Failed",
      message: "Could not initiate OCR job.",
      color: "red",
    });
  };

  return useMutation({
    mutationFn: () => startDocumentOCR(Number(docId)),
    onSuccess: handleSuccess,
    onError: handleError,
    retry: false,
  });
};
